import { useQueryClient } from '@tanstack/react-query'
import { UniSkuEnum } from '@vendia/management-api-types'
import { RegisterDocument } from '@vendia/control-plane-api'
import debug from 'debug'
import { useState } from 'react'
import { useRecoilValue } from 'recoil'
import safe from 'safe-await'
import Button from 'src/components/buttons/button'
import MultiStepFlow from 'src/components/flows/multi-step-flow'
import BasicModal from 'src/components/modals/modal.basic'
import { getEmptySchema } from 'src/components/schema-designer/utils'
import { useDevSettings } from 'src/pages/developer-settings.page'
import analytics, { EVENTS } from 'src/utils/analytics'
import useControlPlaneApi from 'src/utils/hooks/use-control-plane-api'
import { refetchUnis } from 'src/utils/hooks/use-list-unis'
import { generateShortId } from 'src/utils/short-id'
import { userState } from 'src/utils/state'
import { validateSchemaAndDisplayErrors } from 'src/utils/validate-schema'

import { StepId } from '../config'
import { buildUniFqn, buildUniNameFromAlias, generateNodesForApi } from '../utils'
import { StepAddNodes } from './add-nodes.step'
import { StepConfirmation } from './confirmation.step'
import { StepCreateUni } from './create-uni.step'

const logger = debug('app:standard-flow')

type CreateErrorInfo = {
  title: string
  subTitle: string
  lines: string[]
} | null

export type UniCreateFormValues = {
  randomSuffix: string
  invites: { email: string }[]
  nodes: any[]
  uniName: string
  uniNamespace: string
  schema: string
  enableFinancialServices?: boolean
  uniJoinMode?: boolean
}

const StandardFlow = () => {
  const { devSettings } = useDevSettings()
  const defaultNamespace = devSettings?.defaultUniNamespace
  const [createErrorInfo, setCreateErrorInfo] = useState<CreateErrorInfo>(null)
  const userData = useRecoilValue<any>(userState)
  const queryClient = useQueryClient()
  const client = useControlPlaneApi()

  const schema = getEmptySchema()

  async function onSubmit({ value, formApi }: any) {
    logger('createUni!')
    logger('value:', value)

    const { uniName, uniNamespace, nodes, enableFinancialServices, randomSuffix } = value

    // The "name" user entered is actually stored as the alias
    const alias = uniName
    const generatedUniName = buildUniNameFromAlias(alias, randomSuffix)
    const name = buildUniFqn(generatedUniName, uniNamespace)

    // This SHOULD be valid, but let's check once more here (plus we want a few details for analytics)
    const newSchema = JSON.stringify(schema)
    const [e] = await validateSchemaAndDisplayErrors(newSchema)
    if (e) throw e

    const nodesForApi = generateNodesForApi(userData.email, nodes)
    logger({ nodesForApi })

    let results: any[] = []
    const [error, resp] = await safe(
      client.graphqlFetch({ query: RegisterDocument, variables: {
        name,
        alias,
        schema: newSchema,
        initState: '{}',
        nodes: nodesForApi,
        privateFeatures: devSettings?.privateFeatures?.length ? devSettings.privateFeatures : undefined,
        sku: enableFinancialServices ? UniSkuEnum.ShareFinancialServices : UniSkuEnum.Share,
        // asRole
      } }),
    )

    if (error) {
      logger('API err', error)
    }

    results = results.concat(resp)
    logger('Creation results:', results)

    const apiErrors = results
      .filter((res) => {
        return res.errors
      })
      .reduce((acc, curr) => {
        logger('Creation errors', curr.errors)
        acc = acc.concat(curr.errors)
        return acc
      }, [])

    if (apiErrors.length) {
      logger('apiErrors', apiErrors)
      const plural = apiErrors.length > 1 ? 's' : ''

      const errorsFromApi = apiErrors.map((apiError: Error) => apiError.message)
      setCreateErrorInfo({
        title: 'Project creation errors',
        subTitle: `Encountered the following error${plural} while attempting to deploy this project:`,
        lines: errorsFromApi,
      })

      logger('API errors', errorsFromApi)

      // Track failure. @TODO send error messages through?
      analytics.track(EVENTS.UNI_CREATE_FAILED, { uniName: name, nodeCount: nodesForApi.length })

      // exit early
      throw new Error('Project creation failed')
    }

    // Success continue
    analytics.track(EVENTS.UNI_CREATED, {
      uniName: name,
      schemaTemplate: null,
      schemaTitle: schema?.title,
      schemaEntityCount:
        typeof schema?.properties === 'object' && schema?.properties !== null
          ? Object.keys(schema.properties).length
          : null,
      nodeCount: nodesForApi.length,
      authType: nodesForApi.map((node) => node?.settings?.apiSettings?.auth?.authorizerType),
      sku: enableFinancialServices ? 'ShareFinancialServices' : 'Share',
    })

    // Invalidate queries
    refetchUnis({ queryClient })

    logger('on submit completed')
  }

  return (
    <>
      <MultiStepFlow<UniCreateFormValues>
        key='standard'
        onSubmit={onSubmit}
        initialFlowState={{
          // So the flow will have a stable random uni name suffix we can show them in naming
          // step and use in the final step to generate the uni
          randomSuffix: generateShortId({ existingIds: [] }),
          invites: [],
          uniNamespace: defaultNamespace, // QOL change for devs
        }}
        confirmationStep={StepId.Confirmation}
        flowSteps={[
          {
            id: StepId.CreateNetwork,
            navTitle: 'Create Project',
            StepComponent: StepCreateUni,
          },
          {
            id: StepId.AddNodes,
            navTitle: 'Add a workspace',
            StepComponent: StepAddNodes,
          },
          {
            id: StepId.Confirmation,
            navTitle: '',
            showInStepNav: false,
            StepComponent: StepConfirmation,
          },
        ]}
      />

      <BasicModal
        title={createErrorInfo?.title}
        isOpen={createErrorInfo !== null}
        onClose={() => setCreateErrorInfo(null)}
        buttons={
          <Button
            key='cancel'
            kind='secondary'
            onClick={() => {
              setCreateErrorInfo(null)
            }}
          >
            Close
          </Button>
        }
      >
        <p className='mb-2 font-bold'>{createErrorInfo?.subTitle}</p>
        <ul>
          {createErrorInfo?.lines?.map((line, i) => {
            return <li key={i}>{line}</li>
          })}
        </ul>
      </BasicModal>
    </>
  )
}

export default StandardFlow
